import {
  excludedFormFields,
  SELECT_FIELD_TYPES,
  DATE_FIELD_TYPES,
  specialMultiValueFields,
} from "@/src/constants/crmConstants.js";
import { formatTimeSinceChange } from "@/src/utils/dateTimeUtils";

export const extractDomainString = (value) => {
  if (!value || value === "" || value === "[]") return "";
  if (Array.isArray(value)) {
    if (value.length === 0) return "";
    const first = value[0];
    if (!first) return "";
    return typeof first === "string" ? first : first?.domain || "";
  }
  if (typeof value === "object") {
    return value.domain || value.rootdomain || "";
  }
  return typeof value === "string" ? value : "";
};

export const extractEmailString = (value) => {
  if (!value || value === "" || value === "[]") return "";
  if (Array.isArray(value)) {
    if (value.length === 0) return "";
    const first = value[0];
    if (!first) return "";
    return typeof first === "string" ? first : first?.email || "";
  }
  if (typeof value === "object") {
    return value.email || "";
  }
  return typeof value === "string" ? value : "";
};

export const extractPhoneString = (value) => {
  if (!value || value === "" || value === "[]") return "";
  if (Array.isArray(value)) {
    if (value.length === 0) return "";
    const first = value[0];
    if (!first) return "";
    return typeof first === "string"
      ? first
      : first?.original_phone_number || first?.normalized_phone_number || "";
  }
  if (typeof value === "object") {
    return value.original_phone_number || value.normalized_phone_number || "";
  }
  return typeof value === "string" ? value : "";
};

export const convertDomainToApiFormat = (value) => {
  if (typeof value === "string" && value.trim() !== "") {
    return [{ domain: value, rootdomain: value }];
  }
  return value;
};

export const isPhoneNumberField = (attribute) => {
  const idLower = String(attribute.id || "").toLowerCase();
  return idLower === "phone_number" && attribute.type === "phone_number";
};

export const isEmailField = (attribute) => {
  const idLower = String(attribute.id || "").toLowerCase();
  return idLower === "email" && attribute.type === "email";
};

export const isFieldReadOnly = (attribute) => {
  const isExcluded = excludedFormFields.some(
    (excluded) =>
      excluded.toLowerCase() ===
      (attribute.id?.toLowerCase() || attribute.slug?.toLowerCase()),
  );
  return isExcluded;
};

export const isFieldEditable = (
  attribute,
  canEditFields = true,
  disabled = false,
) => {
  if (disabled) return false;
  if (!canEditFields) return false;
  const isExcluded = excludedFormFields.some(
    (excluded) =>
      excluded.toLowerCase() ===
      (attribute.id?.toLowerCase() || attribute.slug?.toLowerCase()),
  );
  if (isExcluded) return false;
  if (attribute.is_editable === false) return false;
  if (attribute.type === "formula" || attribute.type === "select_timestamp")
    return false;
  return true;
};

export const formatSelectTimestampValue = (value) => {
  if (!value) return "Time since last update";
  const actualValue = value.value || value;
  const timestamp = actualValue.timestampvalue;
  if (!timestamp) return "Time since last update";
  return formatTimeSinceChange(timestamp);
};

export const customFilterOption = (input, option) => {
  if (option?.label) {
    return option.label.toLowerCase().includes(input.toLowerCase());
  }
  if (option?.value) {
    return String(option.value).toLowerCase().includes(input.toLowerCase());
  }
  return false;
};

export const focusFieldByType = (fieldId, fieldType) => {
  setTimeout(() => {
    const escapedId = CSS.escape(fieldId);

    if (SELECT_FIELD_TYPES.includes(fieldType)) {
      const searchInput = document.querySelector(
        `#${escapedId} .ant-select-selection-search-input`,
      );
      if (searchInput) {
        searchInput.focus();
        return;
      }

      const selectElement = document.querySelector(
        `#${escapedId} .ant-select-selector`,
      );
      if (selectElement) {
        selectElement.click();
        setTimeout(() => {
          const dropdown = document.querySelector(
            ".ant-select-dropdown:not(.ant-select-dropdown-hidden)",
          );
          if (dropdown) {
            const input = dropdown.querySelector(
              ".ant-select-selection-search-input",
            );
            if (input) {
              input.focus();
            }
          }
        }, 50);
      }
    } else if (DATE_FIELD_TYPES.includes(fieldType)) {
      const dateInput = document.querySelector(
        `#${escapedId} .ant-picker-input input`,
      );
      if (dateInput) {
        dateInput.focus();
      }
    } else if (fieldType === "phone_number") {
      const phoneInput = document.querySelector(`#${escapedId} .vti__input`);
      if (phoneInput) {
        phoneInput.focus();
      }
    } else if (fieldType === "textarea") {
      const textareaElement = document.querySelector(`#${escapedId} textarea`);
      if (textareaElement) {
        textareaElement.focus();
      }
    } else {
      const inputElement = document.querySelector(`#${escapedId} input`);
      if (inputElement) {
        inputElement.focus();
      }
    }
  }, 50);
};

// Helper to get actual slug from schema by field id
const getActualSlug = (fieldId, schemaProperties = []) => {
  const schemaAttr = schemaProperties.find((attr) => attr.id === fieldId);
  return schemaAttr?.slug || fieldId;
};

export const transformMultiValueFields = (
  formState,
  originalEntity,
  attributes,
  schemaProperties = [],
) => {
  specialMultiValueFields.forEach((fieldId) => {
    // Get the actual slug from schema (handles renamed fields)
    const actualSlug = getActualSlug(fieldId, schemaProperties);

    const currentValue = formState[actualSlug];
    const originalValue = originalEntity[actualSlug];
    const currentJson = JSON.stringify(currentValue || []);
    const originalJson = JSON.stringify(originalValue || []);

    if (currentJson !== originalJson) {
      attributes[actualSlug] = currentValue;

      // Sync email field when work_emails changes
      if (fieldId === "work_emails") {
        const workEmails = currentValue || [];
        // Find primary work email or use the first one
        const primaryWorkEmail =
          workEmails.find((e) => e?.is_primary)?.email ||
          workEmails[0]?.email ||
          null;
        // Get the actual slug for email field
        const emailSlug = getActualSlug("email", schemaProperties);
        attributes[emailSlug] = primaryWorkEmail;
      }
    }
  });
};

export const transformCurrentCompanyField = (
  formState,
  originalEntity,
  attributes,
  schemaProperties = [],
) => {
  // Find the actual slug for current_company field by its constant id
  const companySchemaAttr = schemaProperties.find(
    (attr) => attr.id === "current_company",
  );
  const companyFieldSlug = companySchemaAttr?.slug || "current_company";

  // Check if the company field exists in attributes (using the actual slug)
  if (attributes[companyFieldSlug] !== undefined) {
    const companyData = attributes[companyFieldSlug];
    // Check if this is a CREATE (no originalEntity) or UPDATE (has originalEntity)
    const isCreating =
      !originalEntity || Object.keys(originalEntity).length === 0;

    if (companyData && typeof companyData === "object") {
      // Always set the ID for the relationship
      attributes.current_company_id = companyData.id || null;

      if (isCreating) {
        // For CREATE: keep the full object (backend needs it to create the association)
        // Don't modify the field - keep it as the full object
      } else {
        // For UPDATE: send company name as string (for activity log display)
        attributes[companyFieldSlug] = companyData.name || "";
      }
    } else if (companyData === null) {
      attributes.current_company_id = null;
      attributes[companyFieldSlug] = null;
    }
  }
};

export const transformSpecialLeadFields = (
  formState,
  originalEntity,
  attributes,
  schemaProperties = [],
) => {
  const specialLeadFieldIds = ["timezone", "company_location"];

  specialLeadFieldIds.forEach((fieldId) => {
    // Get the actual slug from schema (handles renamed fields)
    const actualSlug = getActualSlug(fieldId, schemaProperties);

    const currentValue = formState[actualSlug];
    const originalValue = originalEntity?.[actualSlug];

    // Only include if the value has changed
    if (currentValue !== originalValue) {
      // Include the field if it has a value or if it's being cleared
      if (currentValue !== undefined) {
        attributes[actualSlug] = currentValue || null;
      }
    }
  });
};

export const applyContactLeadTransformations = (
  formState,
  originalEntity,
  attributes,
  schemaProperties = [],
) => {
  transformMultiValueFields(formState, originalEntity, attributes, schemaProperties);
  transformCurrentCompanyField(
    formState,
    originalEntity,
    attributes,
    schemaProperties,
  );
  transformSpecialLeadFields(formState, originalEntity, attributes, schemaProperties);
};
